import Loader from 'components/Loader';
import usePrompts from 'api/prompts/usePrompts';
import PromptFolderTree from './PromptFolderTree';
import { useState } from 'react';
import PromptAdd from './PromptAdd';
import PromptEdit from './PromptEdit';
import { useParams } from 'react-router';
import { useSearchParams } from 'react-router-dom';
import { useKeyboard } from 'hooks/useKeyboard';
import { FaPlus } from 'react-icons/fa6';
import { SettingsSection } from 'components/settingsHub/SettingsSection';
import { Scope } from 'components/settingsHub/ScopeFilter';
import { PrimaryActionButton, ScopeButton, ScopeGroup } from 'components/settingsHub/settingsEntities.styles';
import { SettingsDetailPanel } from 'components/settingsHub/SettingsDetailPanel';

type Props = {
  projectId?: string | null;
};

const PromptsView = ({ projectId = null }: Props) => {
  const { projectId: routeProjectId, workflowId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const actualProjectId = projectId ?? routeProjectId;
  const [scope, setScope] = useState<Scope>(actualProjectId ? 'project' : 'global');
  const [search, setSearch] = useState('');
  const [unusedOnly, setUnusedOnly] = useState(false);
  const includeGlobalPrompts = scope === 'all' || scope === 'global';

  const { prompts, isLoading } = usePrompts(actualProjectId, includeGlobalPrompts, unusedOnly);
  const [addPromptFormOpen, setAddPromptFormOpen] = useState(false);

  const setSelectedPrompt = (promptId: string | null) => {
    setSearchParams((params) => {
      if (!promptId) {
        params.delete('promptId');
        params.delete('revision');
      } else {
        params.set('promptId', promptId);
      }
      return params;
    });
  };

  useKeyboard({
    keyDown: (e) => {
      if (e.key === 'Escape' && searchParams.get('config')) {
        if (searchParams.get('promptId')) {
          setSelectedPrompt(null);
        } else if (addPromptFormOpen) {
          setAddPromptFormOpen(false);
        } else {
          setSearchParams({});
        }
      }
    },
  });

  if (isLoading) return <Loader />;

  const selectedPromptId = searchParams.get('promptId');
  const query = search.trim().toLowerCase();
  const visiblePrompts = [...(prompts ?? [])]
    .filter((prompt) => !query || prompt.prompt_name.toLowerCase().includes(query))
    .filter((prompt) => {
      if (scope === 'project') return prompt.project_id && prompt.project_id === actualProjectId;
      if (scope === 'global') return !prompt.project_id;
      return true;
    })
    .sort((a, b) => b.created_at.localeCompare(a.created_at));

  return (
    <>
      <SettingsSection
        showScope={!!actualProjectId}
        scope={scope}
        onScopeChange={setScope}
        search={search}
        onSearchChange={setSearch}
        searchPlaceholder="Search prompts…"
        count={visiblePrompts.length}
        countNoun="prompts"
        filters={
          <ScopeGroup>
            <ScopeButton
              type="button"
              selected={unusedOnly}
              aria-pressed={unusedOnly}
              onClick={() => setUnusedOnly((prev) => !prev)}
            >
              Unused
            </ScopeButton>
          </ScopeGroup>
        }
        actions={
          <PrimaryActionButton type="button" onClick={() => setAddPromptFormOpen(true)}>
            <FaPlus size={12} />
            Add Prompt
          </PrimaryActionButton>
        }
      >
        <PromptFolderTree prompts={visiblePrompts} forceExpand={!!query} onSelectPrompt={setSelectedPrompt} />
      </SettingsSection>

      {addPromptFormOpen && (
        <SettingsDetailPanel onClose={() => setAddPromptFormOpen(false)}>
          <PromptAdd
            projectId={actualProjectId ?? ''}
            onCancel={() => setAddPromptFormOpen(false)}
            onSubmit={() => setAddPromptFormOpen(false)}
          />
        </SettingsDetailPanel>
      )}

      {selectedPromptId && (
        <SettingsDetailPanel onClose={() => setSelectedPrompt(null)}>
          <PromptEdit
            promptId={selectedPromptId}
            projectId={actualProjectId ?? ''}
            activeWorkflowId={workflowId}
            onCancel={() => setSelectedPrompt(null)}
            onSubmit={() => setSelectedPrompt(null)}
          />
        </SettingsDetailPanel>
      )}
    </>
  );
};

export default PromptsView;
